/**
 * Main Layout
 *
 * Layout wrapper with sidebar navigation (Foundry design).
 * Sidebar collapses on mobile with hamburger toggle.
 */

import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { AlishaFloater } from '@/components/AlishaFloater';
import { CreditWarningBanner } from '@/components/CreditWarningBanner';
import { useWebSocket } from '@/hooks/useWebSocket';
import { colors } from '@/theme';

/** Pages where Alisha floater should be hidden */
const HIDE_Alisha_FLOATER_PATHS = ['/exchange', '/pricing'];

export function Layout() {
  const location = useLocation();
  const showAlishaFloater = !HIDE_Alisha_FLOATER_PATHS.includes(location.pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Ensure WebSocket is connected for all authenticated pages
  useWebSocket();

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: colors.obsidian, fontFamily: 'Inter, sans-serif' }}>
      {/* Mobile header bar */}
      <div
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 lg:hidden"
        style={{ background: colors.ironCharcoal, borderBottom: `1px solid ${colors.forgeSteel}` }}
      >
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg"
          style={{ color: colors.sandstone }}
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <span
          style={{
            color: colors.foundryCopper,
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: '1.1rem',
            letterSpacing: '0.06em',
          }}
        >
          FOUNDRY
        </span>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - hidden on mobile, shown on lg+ */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 lg:relative lg:translate-x-0 lg:h-screen ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      {/* Main content - add top padding on mobile for header bar */}
      <main className="flex-1 p-4 pt-16 lg:p-8 lg:pt-8 overflow-auto">
        <CreditWarningBanner />
        <Outlet />

        {/* Footer */}
        <footer className="mt-16 pt-8 pb-4 border-t" style={{ borderColor: colors.forgeSteel }}>
          <div className="text-center">
            <p className="text-xs" style={{ color: colors.secondaryText, opacity: 0.6 }}>
              © {new Date().getFullYear()} AerwareAI. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-4 mt-2">
              <a
                href="/help"
                className="text-xs transition-colors hover:opacity-80"
                style={{ color: colors.secondaryText, opacity: 0.6 }}
              >
                Help & FAQ
              </a>
              <a
                href="https://aerware.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs transition-colors hover:opacity-80"
                style={{ color: colors.secondaryText, opacity: 0.6 }}
              >
                Privacy
              </a>
              <a
                href="https://aerware.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs transition-colors hover:opacity-80"
                style={{ color: colors.secondaryText, opacity: 0.6 }}
              >
                Terms
              </a>
            </div>
          </div>
        </footer>
      </main>
      {showAlishaFloater && <AlishaFloater />}
    </div>
  );
}
